import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getDirectShopCataloguePath } from "../../utils/shopUrls";

const ReviewForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [bookingId, setBookingId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [booking, setBooking] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [serviceRatings, setServiceRatings] = useState({
    quality: 0,
    timeliness: 0,
    cleanliness: 0,
    value: 0,
  });

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    if (!token) {
      setError(
        "Invalid review link. Please check your email for the correct review link.",
      );
      setLoading(false);
      return;
    }
    fetchBookingDetails();
  }, [token]);

  const fetchBookingDetails = async () => {
    try {
      // Use secure token-based endpoint (no auth required)
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/review-by-token?token=${token}`,
      );

      const bookingData = response.data.booking || response.data;
      setBookingId(bookingData._id); // Set bookingId from response

      // Check if already reviewed
      if (bookingData.reviewId) {
        setError("You have already reviewed this booking");
        setLoading(false);
        return;
      }

      // Check if completed
      if (bookingData.status !== "completed") {
        setError("Can only review completed bookings");
        setLoading(false);
        return;
      }

      setBooking(bookingData);
      setLoading(false);
    } catch (err) {
      // Handle specific error cases
      if (err.response?.data?.error === "invalid_token") {
        setError(
          "This review link has expired or is invalid. Review links expire after 30 days.",
        );
      } else if (err.response?.data?.error === "missing_token") {
        setError(
          "Invalid review link. Please check your email for the correct review link.",
        );
      } else {
        setError(
          err.response?.data?.message || "Failed to load booking details",
        );
      }

      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      setError("Please select a rating");
      return;
    }

    // U2: only require a non-empty review; the old 10-char minimum could
    // block customers who wrote a short but real review. All other sections
    // (detailed ratings, etc.) stay optional.
    if (!reviewText.trim()) {
      setError("Please add a short review before submitting");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      // Use public token-based endpoint (no authentication required)
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/reviews/submit-with-token`,
        {
          reviewToken: token, // Use the secure token from URL
          rating,
          reviewText: reviewText.trim(),
          photos,
          serviceRatings,
        },
      );

      setSuccess(true);
      setTimeout(() => {
        // Redirect to appropriate profile page
        if (
          ["R4", "R7"].includes(booking?.staffId?.role) &&
          booking?.staffId?.slug
        ) {
          // Independent provider - redirect to freelancer profile path
          const independentBasePath =
            booking.staffId.role === "R7" ? "/freelancers" : "/freelancer";
          window.location.href = `${independentBasePath}/${booking.staffId.slug}`;
        } else if (booking?.staffId?.slug) {
          // Shop professional - redirect to professional profile
          window.location.href = `/professional/${booking.staffId.slug}`;
        } else {
          const shopPath = getDirectShopCataloguePath(booking?.shopId);
          if (shopPath) {
            window.location.href = shopPath;
            return;
          }

          // Last resort - home page
          window.location.href = "/";
        }
      }, 2000);
    } catch (err) {
      // Handle specific error cases
      if (err.response?.data?.error === "invalid_token") {
        setError(
          "This review link has expired. Review links are valid for 30 days.",
        );
      } else if (err.response?.data?.error === "already_reviewed") {
        setError("You have already submitted a review for this booking.");
      } else {
        setError(err.response?.data?.message || "Failed to submit review");
      }

      setSubmitting(false);
    }
  };

  const renderStars = (currentRating, onHover, onClick) => {
    return (
      <div style={{ display: "flex", gap: isMobile ? "4px" : "8px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            onMouseEnter={() => onHover && onHover(star)}
            onMouseLeave={() => onHover && onHover(0)}
            onClick={() => onClick && onClick(star)}
            style={{
              fontSize: isMobile ? "28px" : "36px",
              cursor: onClick ? "pointer" : "default",
              color: star <= (currentRating || 0) ? "#fbbf24" : "#d1d5db",
              transition: "all 0.2s",
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f9fafb",
          padding: isMobile ? "16px" : "40px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ fontSize: isMobile ? "11px" : "14px", color: "#6b7280" }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f9fafb",
          padding: isMobile ? "16px" : "40px 20px",
        }}
      >
        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            background: "#fee2e2",
            padding: isMobile ? "12px" : "16px",
            borderRadius: "8px",
            border: "1px solid #fecaca",
          }}
        >
          <p
            style={{
              margin: 0,
              color: "#991b1b",
              fontSize: isMobile ? "11px" : "14px",
            }}
          >
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f9fafb",
          padding: isMobile ? "16px" : "40px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: "600px",
            width: "100%",
            background: "#ffffff",
            padding: isMobile ? "24px 16px" : "40px",
            borderRadius: "12px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "40px" : "48px",
              marginBottom: "16px",
            }}
          >
            ✅
          </div>
          <h2
            style={{
              margin: "0 0 12px 0",
              fontSize: isMobile ? "12px" : "20px",
              fontWeight: "600",
              color: "#111827",
            }}
          >
            Thank You for Your Review!
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: isMobile ? "11px" : "14px",
              color: "#6b7280",
              lineHeight: 1.6,
            }}
          >
            Your feedback helps others make informed decisions. Redirecting...
          </p>
        </div>
      </div>
    );
  }

  const businessName =
    ["R4", "R7"].includes(booking?.staffId?.role)
      ? `${booking.staffId.userId?.firstName || ""} ${booking.staffId.userId?.lastName || ""}`.trim()
      : booking?.shopId?.name || "Business";

  const isFreelancer = ["R4", "R7"].includes(booking?.staffId?.role);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f9fafb",
        padding: isMobile ? "16px" : "40px 20px",
      }}
    >
      <div
        style={{
          maxWidth: "700px",
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
            padding: isMobile ? "20px 16px" : "32px 24px",
            borderRadius: "12px 12px 0 0",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              margin: "0 0 8px 0",
              fontSize: isMobile ? "12px" : "24px",
              fontWeight: "600",
              color: "#ffffff",
            }}
          >
            Rate Your Experience
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: isMobile ? "10px" : "14px",
              color: "rgba(255,255,255,0.9)",
            }}
          >
            {businessName}
          </p>
        </div>

        {/* Booking Details */}
        <div
          style={{
            background: "#ffffff",
            padding: isMobile ? "16px" : "24px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              background: "#f9fafb",
              borderLeft: "4px solid #2980b9",
              padding: isMobile ? "12px" : "16px",
              borderRadius: "6px",
            }}
          >
            <div style={{ marginBottom: "8px" }}>
              <span
                style={{
                  color: "#6b7280",
                  fontSize: isMobile ? "10px" : "12px",
                  display: "inline-block",
                  width: isMobile ? "70px" : "80px",
                }}
              >
                Service:
              </span>
              <span
                style={{
                  color: "#111827",
                  fontSize: isMobile ? "11px" : "13px",
                  fontWeight: "600",
                }}
              >
                {booking?.serviceId?.name || "Service"}
              </span>
            </div>
            <div style={{ marginBottom: "8px" }}>
              <span
                style={{
                  color: "#6b7280",
                  fontSize: isMobile ? "10px" : "12px",
                  display: "inline-block",
                  width: isMobile ? "70px" : "80px",
                }}
              >
                Provider:
              </span>
              <span
                style={{
                  color: "#111827",
                  fontSize: isMobile ? "11px" : "13px",
                  fontWeight: "600",
                }}
              >
                {businessName}
              </span>
            </div>
            <div>
              <span
                style={{
                  color: "#6b7280",
                  fontSize: isMobile ? "10px" : "12px",
                  display: "inline-block",
                  width: isMobile ? "70px" : "80px",
                }}
              >
                Date:
              </span>
              <span
                style={{
                  color: "#111827",
                  fontSize: isMobile ? "11px" : "13px",
                }}
              >
                {new Date(booking?.appointmentDate).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Review Form */}
        <form
          onSubmit={handleSubmit}
          style={{
            background: "#ffffff",
            padding: isMobile ? "20px 16px" : "32px 24px",
            borderRadius: "0 0 12px 12px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}
        >
          {error && (
            <div
              style={{
                background: "#fee2e2",
                padding: isMobile ? "10px" : "12px",
                borderRadius: "6px",
                marginBottom: "20px",
                border: "1px solid #fecaca",
              }}
            >
              <p
                style={{
                  margin: 0,
                  color: "#991b1b",
                  fontSize: isMobile ? "10px" : "12px",
                }}
              >
                {error}
              </p>
            </div>
          )}

          {/* Overall Rating */}
          <div
            style={{
              marginBottom: isMobile ? "24px" : "32px",
              textAlign: "center",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "12px",
                fontSize: isMobile ? "11px" : "15px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              Overall Rating *
            </label>
            {renderStars(hoverRating || rating, setHoverRating, setRating)}
            {rating > 0 && (
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: isMobile ? "10px" : "12px",
                  color: "#6b7280",
                }}
              >
                {rating === 1 && "Poor"}
                {rating === 2 && "Fair"}
                {rating === 3 && "Good"}
                {rating === 4 && "Very Good"}
                {rating === 5 && "Excellent"}
              </p>
            )}
          </div>

          {/* Detailed Ratings */}
          <div
            style={{
              background: "#f9fafb",
              padding: isMobile ? "16px 12px" : "20px 16px",
              borderRadius: "8px",
              marginBottom: isMobile ? "20px" : "24px",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: isMobile ? "11px" : "14px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              Detailed Ratings (Optional)
            </h3>

            {["quality", "timeliness", "cleanliness", "value"].map(
              (category) => (
                <div
                  key={category}
                  style={{
                    marginBottom: "16px",
                    paddingBottom: "16px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: isMobile ? "10px" : "12px",
                      color: "#4b5563",
                      textTransform: "capitalize",
                    }}
                  >
                    {category}
                  </label>
                  <div
                    style={{ display: "flex", gap: isMobile ? "3px" : "6px" }}
                  >
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        onClick={() =>
                          setServiceRatings({
                            ...serviceRatings,
                            [category]: star,
                          })
                        }
                        style={{
                          fontSize: isMobile ? "20px" : "24px",
                          cursor: "pointer",
                          color:
                            star <= serviceRatings[category]
                              ? "#fbbf24"
                              : "#d1d5db",
                        }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Review Text */}
          <div style={{ marginBottom: isMobile ? "20px" : "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: isMobile ? "11px" : "13px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              Your Review *
            </label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience..."
              required
              maxLength={1000}
              rows={isMobile ? 4 : 6}
              style={{
                width: "100%",
                padding: isMobile ? "10px" : "12px",
                fontSize: isMobile ? "11px" : "13px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: isMobile ? "9px" : "11px",
                color: "#9ca3af",
                textAlign: "right",
              }}
            >
              {reviewText.length}/1000 characters
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || rating === 0}
            style={{
              width: "100%",
              padding: isMobile ? "12px" : "14px",
              background:
                rating === 0
                  ? "#d1d5db"
                  : "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontSize: isMobile ? "12px" : "15px",
              fontWeight: "600",
              cursor: rating === 0 || submitting ? "not-allowed" : "pointer",
              boxShadow:
                rating === 0 ? "none" : "0 4px 6px rgba(26, 101, 158, 0.3)",
            }}
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </button>

          <p
            style={{
              margin: "12px 0 0 0",
              fontSize: isMobile ? "9px" : "11px",
              color: "#9ca3af",
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            Your review will be publicly visible after approval to help other
            customers make informed decisions. By submitting a review you agree to
            your first name being shown alongside it - your surname is never
            displayed.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ReviewForm;
